import { EConactAddressType } from '@modules/project-creator/shared/enums/contact-address-type.enum';
import {
  CONFIGURATOR_ADDRESS_FIELDS,
  ConfiguratorTranslateFn,
  EConfiguratorInvoiceType,
  IConfiguratorAddressFields,
  IConfiguratorAnswers,
  IConfiguratorInvitee,
  IConfiguratorInvoiceAddress
} from '../models/configurator.model';
import { hasKnownPropertyAddress } from '../config/configurator-predicates';
import { isValidEmail } from './configurator-rules.helper';

export interface IConfiguratorAddress {
  address?: string;
  postalCode?: string;
  town?: string;
}

/** "Same address as the property" is checked (only counts while the property address is known). */
export const isSameAsProperty = (
  fields: IConfiguratorAddressFields,
  answers: IConfiguratorAnswers
): boolean => !!fields.sameAsProperty && answers[fields.sameAsProperty] === EConactAddressType.AS_PROJECT;

/** The address an address question stands for: its own fields, or the property's when "same as" is checked. */
export const getEffectiveAddress = (
  fields: IConfiguratorAddressFields,
  answers: IConfiguratorAnswers
): IConfiguratorAddress => {
  const source = isSameAsProperty(fields, answers) ? CONFIGURATOR_ADDRESS_FIELDS : fields;
  return {
    address: answers[source.address] as string,
    postalCode: answers[source.postalCode] as string,
    town: answers[source.town] as string
  };
};

/** "Leopoldstraße 20, 80469 München". */
export const formatAddressLine = ({ address, postalCode, town }: IConfiguratorAddress): string =>
  [address, [postalCode, town].filter(Boolean).join(' ')].filter(Boolean).join(', ');

/** Street and postal code, or "same as the property" with a known property address. */
export const isAddressAnswered = (
  fields: IConfiguratorAddressFields,
  answers: IConfiguratorAnswers
): boolean =>
  isSameAsProperty(fields, answers)
    ? hasKnownPropertyAddress(answers)
    : !!answers[fields.address] && !!answers[fields.postalCode];

export const getInvoiceName = (invoice: IConfiguratorInvoiceAddress | null | undefined): string => {
  if (!invoice) return '';
  if (invoice.type === EConfiguratorInvoiceType.PERSON) {
    return [invoice.firstName, invoice.lastName].filter(Boolean).join(' ');
  }
  return invoice.companyName || '';
};

/** A company name, or first and last name of a person; street, postal code and town; a valid e-mail if any. */
export const isInvoiceAddressComplete = (
  invoice: IConfiguratorInvoiceAddress | null | undefined
): boolean => {
  if (!invoice) return false;
  const hasName =
    invoice.type === EConfiguratorInvoiceType.PERSON
      ? !!invoice.firstName && !!invoice.lastName
      : !!invoice.companyName;
  return (
    hasName &&
    !!invoice.address &&
    !!invoice.postalCode &&
    !!invoice.town &&
    (!invoice.email || isValidEmail(invoice.email))
  );
};

/** Cards left completely empty do not count. */
export const isInviteeBlank = (invitee: IConfiguratorInvitee): boolean =>
  !invitee?.firstName && !invitee?.lastName && !invitee?.email;

export const getFilledInvitees = (
  invitees: IConfiguratorInvitee[] | null | undefined
): IConfiguratorInvitee[] => (invitees || []).filter((invitee) => !isInviteeBlank(invitee));

export const isInviteeComplete = (invitee: IConfiguratorInvitee): boolean =>
  !!invitee.firstName && !!invitee.lastName && !!invitee.email && isValidEmail(invitee.email);

export const getInviteeName = (invitee: IConfiguratorInvitee): string =>
  [invitee.firstName, invitee.lastName].filter(Boolean).join(' ') || invitee.email || '';

const T_DONE = 'ProjectConfiguratorV2.Done';

/** Name in "Vielen Dank, Frau Beispiel!": the salutation with the last name, otherwise the full name. */
export const getGreetingName = (
  answers: IConfiguratorAnswers,
  translate: ConfiguratorTranslateFn
): string => {
  if (!answers.lastName) return '';
  if (answers.salutation === 'ms' || answers.salutation === 'mr') {
    return translate(`${T_DONE}.salutation.${answers.salutation}`, { name: answers.lastName });
  }
  return [answers.firstName, answers.lastName].filter(Boolean).join(' ');
};

/** How the contact person calls back: WhatsApp if chosen, otherwise by phone if there is a number. */
export const getCallbackChannel = (answers: IConfiguratorAnswers): 'whatsapp' | 'phone' | null => {
  if (answers.contactChannels?.includes('whatsapp')) return 'whatsapp';
  return answers.phone ? 'phone' : null;
};
